import React, { Fragment, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import Decimal from 'decimal.js';

import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { richText } from '../../util/richText';
import { useConfiguration } from '../../context/configurationContext';
import { ensureListing, ensureUser } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { displayPrice } from '../../util/configHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { priceData } from '../../containers/ListingPage/ListingPage.shared';
import { useListingAttributes } from '../../hooks/useListingAttributes';
import { convertDecimalToMoney, convertMoneyToDecimal } from '../../util/currency';

import { AvatarXSmall } from '../Avatar/Avatar';
import AspectRatioWrapper from '../AspectRatioWrapper/AspectRatioWrapper';
import FieldColor from '../FieldColor/FieldColor';
import IconBin from '../IconBin/IconBin';
import NamedLink from '../NamedLink/NamedLink';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import HelpTooltip from '../HelpTooltip/HelpTooltip';
import { InlineTextButton } from '../Button/Button';

import css from './OrderListing.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, metadata, config, intl } = props;
  const { listingType } = publicData || {};
  const { taxRate } = metadata || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);

  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = useMemo(() => {
    if (!price || typeof taxRate !== 'number') {
      return priceData(price, config.currency, intl);
    }

    const gross = convertMoneyToDecimal(price)
      .times(taxRate + 100)
      .dividedBy(100)
      .toNearest(0.01, Decimal.ROUND_HALF_UP);

    const grossMoney = convertDecimalToMoney(gross, price.currency);

    return priceData(grossMoney, config.currency, intl);
  }, [price, taxRate, config.currency, intl]);

  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="OrderSummary.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

const AttributesMaybe = ({ publicData, intl }) => {
  const attributes = useListingAttributes(publicData, 3);

  if (!attributes.length) {
    return null;
  }

  return (
    <div className={css.attributes}>
      {attributes.map(({ key, value, label, displayValue }) => {
        const colorValue = intl.messages[`ListingField.${key}.${value}.swatch`];
        const helpValue = intl.messages[`ListingField.${key}.${value}.help`];

        return (
          <Fragment key={key}>
            <label className={css.attributeLabel}>{label}:</label>
            <div className={css.attributeValue}>
              {colorValue && <FieldColor value={colorValue} size="medium" withoutLabel />}
              {displayValue}
              {helpValue && <HelpTooltip>{helpValue}</HelpTooltip>}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
};

const OrderListing = ({
  listing,
  renderSizes,
  disabled,
  onRemoveFromCart,
  showAuthorInfo,
  className,
}) => {
  const intl = useIntl();
  const config = useConfiguration();

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData, metadata } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage = currentListing.images?.[0] || null;
  const hasAction = !!onRemoveFromCart;

  const linkProps = { name: 'ListingPage', params: { id, slug } };

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  return (
    <div className={classNames(css.container, hasAction && css.containerWithAction, className)}>
      <NamedLink {...linkProps} className={css.imageWrapper}>
        <AspectRatioWrapper className={css.image} width={aspectWidth} height={aspectHeight}>
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
        </AspectRatioWrapper>
      </NamedLink>
      <NamedLink className={css.title} {...linkProps}>
        {richText(title, {
          longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
          longWordClass: css.longWord,
        })}
      </NamedLink>
      <PriceMaybe
        price={price}
        publicData={publicData}
        metadata={metadata}
        config={config}
        intl={intl}
      />
      {showAuthorInfo ? (
        <div className={css.authorInfo}>
          {author.id && (
            <AvatarXSmall className={css.authorAvatar} user={author} disableProfileLink />
          )}
          <span className={css.authorName}>
            <FormattedMessage id="OrderListing.author" values={{ authorName }} />
          </span>
        </div>
      ) : null}
      <AttributesMaybe publicData={publicData} intl={intl} />
      {hasAction && (
        <InlineTextButton
          type="button"
          className={css.removeFromCartButton}
          disabled={disabled}
          onClick={() => onRemoveFromCart(listing)}
        >
          <span className={css.removeFromCartButtonLabel}>
            <FormattedMessage id="OrderListing.removeFromCart" />
          </span>
          <IconBin className={css.removeFromCartButtonIcon} />
        </InlineTextButton>
      )}
    </div>
  );
};

export default OrderListing;
